// KakaoPayApprovalPage.tsx
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { ROUTES } from '@/routes';
import {
  createMembershipPaymentResultPath,
  createPaymentResultPath,
  getPaymentResultIdFromResponse,
} from '@/utils/membership-payment-result';
import {
  createPendingSubscriptionPayload,
  WEB_PC_MEMBERSHIP_CHANNEL,
} from '@/utils/pending-subscription';
import { getCplatCode } from '@/pages/mobile-membership/mobile-membership-api';
import { createMobileKakaoBillingPayload } from '@/pages/mobile-membership/mobile-membership-utils';

const PC_MEMBERSHIP_PENDING_REGISTRATION_KAKAO_FLOW = 'pcMembershipPendingRegistration';

const KakaoPayApprovalPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: approveKakaoPay } = usePost(CPLAT_API_ROUTES.setSubscriptionForKakaopay);
  const { mutate: registerCardForBilling } = usePost(CPLAT_API_ROUTES.registerCardForBilling);
  const { mutate: requestPendingSubscription } = usePost(
    CPLAT_API_ROUTES.registerPendingSubscription,
  );
  const { CplatToken } = useAppSelector((state) => state.user);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pgToken = params.get('pg_token');
    const kakaoPayRequestInfo = JSON.parse(localStorage.getItem('kakaoPayRequestInfo') || '{}');
    const kakaoPayResponseInfo = JSON.parse(localStorage.getItem('kakaoPayResponseInfo') || '{}');
    // const subscriptionNo = kakaoPayRequestInfo?.SubscriptionNo || 21; // 실제로 저장한 값에 따라 조정
    const subscriptionNo = Number(localStorage.getItem('subscriptionNo'));
    const isMobileMembershipFlow = localStorage.getItem('kakaoPayFlow') === 'mobileMembership';
    const kakaoPayFlow = localStorage.getItem('kakaoPayFlow');
    const isPcPaymentChangeFlow = localStorage.getItem('kakaoPayPaymentChange') === 'true';

    if (pgToken) {
      if (kakaoPayFlow === PC_MEMBERSHIP_PENDING_REGISTRATION_KAKAO_FLOW) {
        registerCardForBilling(
          createMobileKakaoBillingPayload({
            cplatToken: CplatToken,
            pgToken,
            kakaoPayRequestInfo,
            kakaoPayResponseInfo,
          }),
          {
            onSuccess: (response) => {
              if (getCplatCode(response) !== '200') {
                clearPcPendingKakaoPayStorage();
                navigate(`${ROUTES.moreMembership}?pendingSubscription=fail`, { replace: true });
                return;
              }

              requestPendingSubscription(
                createPendingSubscriptionPayload({
                  cplatToken: CplatToken,
                  channel: WEB_PC_MEMBERSHIP_CHANNEL,
                }),
                {
                  onSuccess: (pendingResponse) => {
                    if (getCplatCode(pendingResponse) !== '200') {
                      clearPcPendingKakaoPayStorage();
                      navigate(`${ROUTES.moreMembership}?pendingSubscription=fail`, {
                        replace: true,
                      });
                      return;
                    }

                    clearPcPendingKakaoPayStorage();
                    navigate(`${ROUTES.moreMembership}?pendingSubscription=success`, {
                      replace: true,
                    });
                  },
                  onError: () => {
                    clearPcPendingKakaoPayStorage();
                    navigate(`${ROUTES.moreMembership}?pendingSubscription=fail`, {
                      replace: true,
                    });
                  },
                },
              );
            },
            onError: () => {
              clearPcPendingKakaoPayStorage();
              navigate(`${ROUTES.moreMembership}?pendingSubscription=fail`, { replace: true });
            },
          },
        );
        return;
      }

      approveKakaoPay(
        {
          cplatToken: CplatToken,
          subscriptionNo,
          pgToken,
          kakaoPayRequestInfo,
          kakaoPayResponseInfo,
        },
        {
          onSuccess: (response) => {
            const paymentResultId = getPaymentResultIdFromResponse(response);

            if (isMobileMembershipFlow) {
              localStorage.removeItem('kakaoPayFlow');
              localStorage.setItem('mobileMembershipPaymentMethod', '카카오페이');
              navigate(createPaymentResultPath(ROUTES.mobileMembershipComplete, paymentResultId), {
                replace: true,
              });
              return;
            }
            // 결제 성공 안내 및 구독정보 갱신
            localStorage.removeItem('kakaoPayPaymentChange');
            navigate(
              createMembershipPaymentResultPath({
                path: ROUTES.moreMembership,
                result: 'success',
                paymentChange: isPcPaymentChangeFlow,
                paymentResultId,
              }),
            );
          },
          onError: () => {
            if (isMobileMembershipFlow) {
              localStorage.removeItem('kakaoPayFlow');
              navigate(`${ROUTES.mobileKakaoPayCancel}?result=fail`, { replace: true });
              return;
            }
            localStorage.removeItem('kakaoPayPaymentChange');
            navigate(
              createMembershipPaymentResultPath({
                path: ROUTES.moreMembership,
                result: 'fail',
                paymentChange: isPcPaymentChangeFlow,
              }),
            );
          },
        },
      );
    }
  }, [
    CplatToken,
    approveKakaoPay,
    location.search,
    navigate,
    registerCardForBilling,
    requestPendingSubscription,
  ]);

  return <GlobalSpinner />;
};

export default KakaoPayApprovalPage;

const clearPcPendingKakaoPayStorage = () => {
  localStorage.removeItem('kakaoPayFlow');
  localStorage.removeItem('kakaoPayPendingSubscriptionChannel');
  localStorage.removeItem('kakaoPayPaymentChange');
  localStorage.removeItem('kakaoPayRequestInfo');
  localStorage.removeItem('kakaoPayResponseInfo');
  localStorage.removeItem('subscriptionNo');
};
